import { useState } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";
import * as Icons from "lucide-react";
import { FAQCategory } from "@/types/faq";
import { cn } from "@/lib/utils";
import { ScrollArea } from "@/components/ui/scroll-area";

interface FAQSidebarProps {
  categories: FAQCategory[];
  selectedCategory: string;
  selectedSubcategory?: string;
  onCategorySelect: (categoryId: string) => void;
  onSubcategorySelect: (categoryId: string, subcategoryId: string) => void;
}

export const FAQSidebar = ({
  categories,
  selectedCategory,
  selectedSubcategory,
  onCategorySelect,
  onSubcategorySelect,
}: FAQSidebarProps) => {
  const [expandedCategories, setExpandedCategories] = useState<string[]>([
    selectedCategory,
  ]);

  const toggleCategory = (categoryId: string) => {
    setExpandedCategories((prev) =>
      prev.includes(categoryId)
        ? prev.filter((id) => id !== categoryId)
        : [...prev, categoryId]
    );
  };

  const getIcon = (iconName: string) => {
    const Icon = Icons[iconName as keyof typeof Icons] as Icons.LucideIcon;
    return Icon || Icons.HelpCircle;
  };

  return (
    <ScrollArea className="h-[calc(100vh-12rem)]">
      <div className="space-y-1 pr-4">
        {/* All Categories */}
        <button
          onClick={() => onCategorySelect("all")}
          className={cn(
            "w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-colors",
            selectedCategory === "all"
              ? "bg-primary text-primary-foreground"
              : "text-foreground hover:bg-muted"
          )}
        >
          <Icons.Grid3x3 className="w-4 h-4" />
          Toate categoriile
        </button>

        <div className="h-px bg-border my-2" />

        {/* Categories with subcategories */}
        {categories.map((category) => {
          const Icon = getIcon(category.icon);
          const isExpanded = expandedCategories.includes(category.id);
          const isSelected = selectedCategory === category.id;

          return (
            <div key={category.id} className="space-y-1">
              <div className="flex items-center">
                <button
                  onClick={() => {
                    onCategorySelect(category.id);
                    if (!isExpanded) {
                      toggleCategory(category.id);
                    }
                  }}
                  className={cn(
                    "flex-1 flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-colors",
                    isSelected && !selectedSubcategory
                      ? "bg-primary/10 text-primary"
                      : "text-foreground hover:bg-muted"
                  )}
                >
                  <Icon className="w-4 h-4" />
                  <span className="flex-1 text-left">{category.title}</span>
                </button>
                {category.subcategories &&
                  category.subcategories.length > 0 && (
                    <button
                      onClick={() => toggleCategory(category.id)}
                      className="p-2 hover:bg-muted rounded-lg transition-colors"
                    >
                      {isExpanded ? (
                        <ChevronDown className="w-4 h-4" />
                      ) : (
                        <ChevronRight className="w-4 h-4" />
                      )}
                    </button>
                  )}
              </div>

              {/* Subcategories */}
              {isExpanded && category.subcategories && (
                <div className="ml-4 space-y-1 pl-4 border-l-2 border-border">
                  {category.subcategories.map((sub) => (
                    <button
                      key={sub.id}
                      onClick={() => onSubcategorySelect(category.id, sub.id)}
                      className={cn(
                        "w-full flex items-center justify-between px-4 py-2 rounded-lg text-sm transition-colors",
                        selectedSubcategory === sub.id
                          ? "bg-primary/10 text-primary font-medium"
                          : "text-muted-foreground hover:bg-muted hover:text-foreground"
                      )}
                    >
                      <span>{sub.title}</span>
                      {sub.count && (
                        <span className="text-xs text-muted-foreground">
                          {sub.count}
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </ScrollArea>
  );
};
